"use strict";

/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, June 19, 2014
 * For information or permission request, email info@aetonixsystems.com
 */
var window = require("global");

var EventEmitter = require("eventemitter2").EventEmitter2;
var Promise = require("promise6");
var ReactNative = require("react-native");
var Dimensions = ReactNative.Dimensions;
var PeerConnection = window.RTCPeerConnection;
var optimize = require("./sdp_optimization");
var analyzeStats = require("./analyzeStats").analyzeStats;
var xtend = require("xtend");
var par = require("par");
var Deferred = par(require("promise-defer"), Promise);

var cached_stream = null;
var cached_stream_id = "";
var cached_stream_count = 0;
var releaseWait = null;

function releaseStream() {
	cached_stream_count--;
	if(!cached_stream_count) {
		return cached_stream
			.then(function(stream){
				if (stream.stop) {
				// Older Chrome
					stream.stop();
				} else {
				// Newer Chrome
					var tracks = stream.getTracks();
					tracks.forEach(function (track) {
						track.stop();
					});
				}
			})
			.then(function(){
				releaseWait.resolve();
				releaseWait = null;
				cached_stream = null;
				cached_stream_id = "";
			});
	}
}

function getStream(options) {
	var id = getSourceId(options);
	if(!cached_stream) {
		cached_stream_id = id;
		cached_stream = get_local(options).catch(handle_invalid);
		cached_stream_count++;
		releaseWait = new Deferred();
		return cached_stream;
	}
	if(cached_stream_id !== id) {
		return releaseWait.promise.then(par(getStream, options));
	}	else {
		cached_stream_count++;
		return Promise.resolve(cached_stream);
	}

	function handle_invalid(e) {
		var isNotFound = (e.name === "NotFoundError");
		var constraint = e.constraint;
		var isDeviceConstraint = (constraint === "deviceId");
		var isOverconstrained = (e.name === "OverconstrainedError");

		if (isNotFound || (isOverconstrained && isDeviceConstraint)) {
			// Clear input options if they are invalid
			options.videoSource = "";
			options.audioSource = "";

			window.localStorage.video_input = "";
			window.localStorage.audio_input = "";

			return get_local(options).catch(handle_invalid);
		}

		var RESOLUTION_FIELDS = [
			"width",
			"minWidth",
			"height",
			"minHeight"
		];

		if (isOverconstrained && (RESOLUTION_FIELDS.indexOf(constraint) !== -1)) {
			return get_local(xtend(options, {
				bigger: true
			})).catch(handle_invalid);
		}

		return get_local(xtend(options, {
			bigger: true,
			noConstraints: true,
			videoSource: "",
			audioSource: ""
		}));
	}
}

function getSourceId(options) {
	return "" + options.videoSource + options.audioSource + !!options.bigger;
}

/**
 * @class  browser
 */
module.exports = {
	type: "browser",
	init: init,
	RTC: RTC
};

/**
 * This is the bare bones for an RTC adapter for use with Buzz
 * Events:
 * ice: Local ICE candidate available: {
 * 		type:"candidate",
 * 		candidate: "iceCandidate.candidate",
 * 		label:"iceCandidate.sdpMLineIndex",
 * 		"id":"iceCandidate.sdpMid"
 * 	}
 * sdp: Local SDP data: {type:"offer",sdp:"sdp.description"},
 * stream: Remote stream has been received
 * close: The connection has been closed
 * error: There was some sort of error
 *
 * @memberOf browser
 * @param options {Object} contains options for creating the call {
 * 		iceServers:"ice servers",
 * 		local:"video tag for local stream",
 * 		remote:"video tag for remote stream",
 * 		isInitiator: "boolean for whether the user is the caller or not"
 * 	}
 */
function RTC(options) {
	var self = this;
	this.options = options;
	this.limitBitrate = false;
	this.dropped = 3;
	var ice = options.iceServers || [{
		"url": "stun:stun.1.google.com:19302"
	}];
	console.debug("Making PeerConnection with ICE:", ice);
	EventEmitter.call(this);
	this._onerr = function (err) {
		self.clean();
		self.emit("error", err);
	};

	this.pc = new PeerConnection({
		iceServers: ice
	});
	this._initEvents();
	this.pc.addStream(options._local);
	if (options.isInitiator) {
		console.debug("Is initiator");
		setTimeout(function () {
			self._sendOffer().catch(self._onerr);
		});
	} else console.debug("Not initiator");
}

// The RTC object inherits from EventEmitter
RTC.prototype = new EventEmitter();

// Declare future properties to create hidden class
RTC.prototype.options = null;
RTC.prototype.pc = null;
RTC.prototype.dropped = 3;
RTC.prototype.limitBitrate = false;

RTC.prototype.limitBandwidth = function(){
	if(this.dropped !== 1) this.dropped--;
	this.limitBitrate = true;
};

/**
 * Get the stats for the peerconnection (has it connected, quality, etc)
 *
 * @memberof  adapter
 * @return {Promise}  Resolves to object with the current `quality` and whether it `hasData`
 */
RTC.prototype.getStats = function () {
	return this.pc.getStats().then(function(stats) {
		return analyzeStats(stats);
	});
};

/**
 * Add remote ICE candidates, processes them for consumption by RTCPeerConnection
 *
 * @memberOf browser
 * @param {String} ice The ice data that was received from the remote user
 * @return {Promise}   Resolves if adding the candidate was successful
 */
RTC.prototype.addICE = function (ice) {
	var pc = this.pc;
	return new Promise(function (resolve, reject) {
		console.debug("Adding ICE candidate", ice);
		pc.addIceCandidate(new window.RTCIceCandidate({
			candidate: ice.candidate || "candidate",
			sdpMLineIndex: ice.label,
			sdpMid: ice.id
		}), resolve, reject);
	});
};

/**
 * Set the remote SDP. If the user is a caller, this must be an answer, else it must be an offer
 *
 * @memberOf browser
 * @param {String} sdp SDP data that was received from the remote user
 * @return {Promise} Resolves if adding the SDP worked, rejects if it didn't
 */
RTC.prototype.addSDP = function (sdp) {
	var filtered = this.filterIncomingSDP(sdp);
	var initiator = this.options.isInitiator;
	if (initiator) return this._handleAnswer(filtered);
	else return this._handleOffer(filtered);
};

/**
 * Function that takes the local SDP string and returns a modifed SDP string before the `sdp` event occures
 *
 * @memberOf browser
 * @param {String} sdp The local SDP string
 * @return {String} The modified SDP string
 */
RTC.prototype.filterSDP = function (sdp) {
	var initiator = this.options.isInitiator;

	sdp = optimize(sdp, initiator, this.limitBitrate, this.dropped);

	// Filter out SDP if using an old version of Chrome
	if (isOld(46))
		sdp = sdp
			.replace(/ useinbandfec=1/g, "")
			.replace(/a=rtpmap:\d+ rtx\/\d+\r\n/ig, "")
			.replace(/a=fmtp:\d+ apt=\d+\r\n/ig, "");

	return sdp;
};

/**
 * Function that takes the remote SDP string and returns a modified SDP string
 * @param  {[type]} sdp [description]
 * @return {[type]}     [description]
 */
RTC.prototype.filterIncomingSDP = function (sdp) {
	if (!isOld(46)) return sdp;
	if (sdp.type === "answer") return sdp;

	// Fix the SDP up before processing it so that we don't get a parse error for old chrome
	return {
		type: sdp.type,
		sdp: optimize.legacy(sdp.sdp)
	};
};

/**
 * Cleans up any resources to destroy this object
 *
 * @memberOf browser
 * @return {Promise} Resolves after cleaning is finished
 */
RTC.prototype.clean = function () {
	console.debug("Cleaning Browser RTC");
	return Promise.resolve().then(function () {
		var pc = this.pc;
		if (pc && this._isOpen()) return this._waitClose();
	}.bind(this)).then(function () {
		if(this._remote) {
			this._remote = null;
		}
		this.emit("close");

		var options = this.options;
		if (!options) return;
		if (options.local) options.local.src = "";
		if (options.remote) options.remote.src = "";
		if(options._local) {
			options._local = null;
			return releaseStream(options);
		}
	}.bind(this));
};

RTC.prototype._waitClose = function () {
	if(isOld(46))
		return Promise.resolve();

	return new Promise(function (resolve) {
		if (!this._isOpen()) resolve();

		var pc = this.pc;
		pc.close();
		pc.oniceconnectionstatechange = checkIfClosed.bind(this);

		function checkIfClosed() {
			if (!this._isOpen()) resolve();
		}
	}.bind(this));
};

/**
 * [_isOpen description]
 *
 * @memberOf browser
 * @return {Boolean} [description]
 */
RTC.prototype._isOpen = function () {
	if (this.pc.signalingState && (this.pc.signalingState === "closed"))
		return false;
	return this.pc.iceConnectionState !== "closed";
};

/**
 * Creates an SDP offer and sends emits sdp event
 *
 * @memberOf browser
 * @return {Promise} Resolves if it was successful
 */
RTC.prototype._sendOffer = function () {
	var pc = this.pc;
	var self = this;
	return new Promise(function (resolve, reject) {
		pc.createOffer(function (offer) {
			console.debug("Created offer", offer);
			var sdp = self.filterSDP(offer.sdp);
			var newOffer = new window.RTCSessionDescription({
				type: offer.type,
				sdp: sdp
			});
			pc.setLocalDescription(newOffer, function () {
				self.emit("sdp", {
					type: offer.type,
					sdp: sdp
				});
				console.debug("Emitted offer", newOffer);
				resolve();
			}, reject);
		}, reject);
	});
};

/**
 * Initializes event listeners on the RTCPeerConnection
 * The events that are listened to are:
 * onaddstream: Notifies client that the remote streamw as received
 * onicecandidate: Handles a local ice candidate being found, emits event
 * oniceconnectionstatechange: Used to dectect disconnection
 * onremovestream: When the stream is removed, cleans
 *
 * @memberOf browser
 */
RTC.prototype._initEvents = function () {
	console.debug("Listening to PeerConnection events");
	var pc = this.pc;
	pc.onaddstream = this._handleStream.bind(this);
	pc.onicecandidate = this._handleLocalIce.bind(this);
	pc.oniceconnectionstatechange = this._handleConnectionStateChange.bind(this);
	pc.onremovestream = this.clean.bind(this);
};

/**
 * Listens for the iceConnectionState to be disconnected, and then treats it as a close
 *
 * @memberOf browser
 */
RTC.prototype._handleConnectionStateChange = function () {
	var state = this.pc.iceConnectionState;
	console.debug("Changed connection state", state);
	if (["closed", "disconnected", "failed"].indexOf(state) !== -1)
		this.clean();
};

/**
 * Handles local ice events
 *
 * @memberOf browser
 * @param {RTCIceCandidate} ice Raw ice candidate
 */
RTC.prototype._handleLocalIce = function (event) {
	var ice = event.candidate;
	console.debug("Handling local ICE candidate", ice);
	if (ice && this._isOpen()) this.emit("ice", {
		type: "candidate",
		candidate: ice.candidate,
		label: ice.sdpMLineIndex,
		id: ice.sdpMid
	});
};

/**
 * Handles the SDP offer sent from Buzz and creates response SDP, emits sdp event
 *
 * @memberOf browser
 * @param {Object} sdp The data from the SDP event
 * @return {Promise} Resolves if everything was successful
 */
RTC.prototype._handleOffer = function (sdp) {
	var pc = this.pc;
	var self = this;
	return new Promise(function (resolve, reject) {
		console.debug("Handling SDP offer", sdp);
		pc.setRemoteDescription(new window.RTCSessionDescription({
			type: sdp.type,
			sdp: sdp.sdp
		}), function () {
			pc.createAnswer(function (answer) {
				console.debug("Created SDP answer", answer);
				answer.sdp = self.filterSDP(answer.sdp);
				pc.setLocalDescription(new window.RTCSessionDescription(answer), function () {
					self.emit("sdp", {
						sdp: answer.sdp,
						type: answer.type
					});
					console.debug("Set local description", answer);
					resolve();
				}, reject);
			}, reject);
		}, reject);
	});
};

/**
 * Handles the SDP answer sent from Buzz and translates it to be used with RTCPeerConnection
 *
 * @memberOf browser
 * @param {Object} sdp The data from the SDP event
 */
RTC.prototype._handleAnswer = function (sdp) {
	var pc = this.pc;
	return new Promise(function (resolve, reject) {
		console.debug("Handling SDP answer", sdp);
		pc.setRemoteDescription(new window.RTCSessionDescription({
			type: sdp.type,
			sdp: sdp.sdp
		}), resolve, reject);
	});
};

/**
 * Handles incoming remote streams and starts displaying them / emits an event
 *
 * @memberOf browser
 * @param {MediaStream} stream The remote media stream
 */
RTC.prototype._handleStream = function (event) {
	var stream = event.stream;
	var options = this.options;
	this._remote = stream;

	this.emit("stream", {
		local: options._local,
		remote: stream
	});
};

RTC.prototype.switchCamera = function (parent){
	return Promise.resolve().then(function(){
		parent.renegotiate("video");
	});
};

/**
 * Initializes a new RTC instance, also gets the camera stream
 *
 * @memberOf browser
 * @param  {Object} options Options taken by the RTC constructor. Written at top of file
 * @return {Promise}        Resolves to RTC instance
 */
function init(options) {
	options = options || {};
	console.debug("Initializing Browser RTC", options);

	var audioSource = window.localStorage.audio_input;
	var videoSource = window.localStorage.video_input;

	options.audioSource = audioSource;
	options.videoSource = videoSource;

	return getStream(options).then(function (stream) {
		console.debug("Got local stream", stream);
		options._local = stream;
		return new RTC(options, false);
	});
}

	/**
	 * Gets the local stream, basically calls getUserMedia and wraps it in a Promise
	 *
	 * @memberOf browser
	 * @return {Promise} Resolves to stream
	 */
function get_local(options) {
	var use_bigger = options.bigger;
	var noConstraints = options.noConstraints;

	var loadingPromise = new Promise(function (resolve, reject) {
		var audioConstraints = {
			googAutoGainControl: false,
			googAutoGainControl2: false,
			googEchoCancellation: true,
			googEchoCancellation2: true,
			googNoiseSuppression: true,
			googNoiseSuppression2: true,
			googHighpassFilter: true,
			googTypingNoiseDetection: true,
			googAudioMirroring: true,
			googDucking: false,
		};

		var maxWidth = (use_bigger ? 640 : 320);
		var maxHeight = (use_bigger ? 480 : 240);
		var minVideo = 200;

		if(isPortrait()){
			var swap = maxWidth;
			maxWidth = maxHeight;
			maxHeight = swap;
		}

		var videoConstraints = {
			width: {
				min: minVideo,
				ideal: 320,
				max: maxWidth,
			},
			height: {
				min: minVideo,
				ideal: 320,
				max: maxHeight,
			},
			frameRate: {
				min: 1,
				max: 30
			}
		};

		var audioSource = options.audioSource;
		if (audioSource && (audioSource !== "null") && (audioSource !== "undefined"))
			audioConstraints.deviceId = {
				exact: audioSource
			};

		var videoSource = options.videoSource;
		if (videoSource && (videoSource !== "null") && (videoSource !== "undefined"))
			videoConstraints.deviceId = {
				exact: videoSource
			};

		var constraints = {
			audio: audioConstraints,
			video: videoConstraints
		};

		var voiceOnly = (videoSource === "false") || options.voiceOnly;

		if(noConstraints)
			constraints = {
				audio: true,
				video: true,
			};

		if (voiceOnly)
			constraints.video = false;

		window.navigator.getUserMedia(constraints, resolve, reject);
	});

	return loadingPromise;
}

function isOld(version) {
	var userAgentMatch = window.navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
	var chromeVersion = userAgentMatch ? parseInt(userAgentMatch[2], 10) : false;

	// If we're not even running in chrome, don't bother with this fix
	if (!chromeVersion) return false;

	// If we're in a recent version of chrome, don't bother with the fix
	if (chromeVersion >= version) return false;

	return true;
}

function isPortrait() {
	var size = Dimensions.get("window");
	return size.width < size.height;
}
